import { useState } from "react";
import { DateUtils } from '@cleanpro/common-ui';
import { useNavigate } from "react-router-dom";
import { format, isToday, isSameMonth } from "date-fns";
import { ChevronLeft, ChevronRight, Calendar, Filter } from "lucide-react";
import { useSchedule } from "../hooks/useSchedule";
import { ViewMode } from "../types";

export default function AdvancedSchedule() {
  const navigate = useNavigate();
  const {
    currentDate,
    viewMode,
    showJobs,
    showQuotes,

    setViewMode,
    setShowJobs,
    setShowQuotes,
    navigateDate,
    goToToday,
    dateRange,
    calendarDays,
    getItemsForDate,
  } = useSchedule();

  const [showFilters, setShowFilters] = useState(false);

  const viewModes: { id: ViewMode; label: string }[] = [
    { id: "month", label: "Month" },
    { id: "week", label: "Week" },
    { id: "day", label: "Day" },
  ];

  const formatDateHeader = () => {
    switch (viewMode) {
      case "month":
        return format(currentDate, "MMMM yyyy");
      case "week":
        return `${format(dateRange.start, "MMM d")} - ${format(
          dateRange.end,
          "MMM d, yyyy"
        )}`;
      case "day":
        return format(currentDate, "EEEE, MMMM d, yyyy");
      default:
        return format(currentDate, "MMMM yyyy");
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleItemClick = (item: any) => {
    if (item._type === "job") {
      navigate(`/jobs/${item.id}`);
    } else if (item._type === "quote") {
      navigate(`/quotes/${item.id}`);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCalendarItem = (item: any) => {
    console.log("Rendering item:", item);
    const isJob = item._type === "job";
    const bgColor = isJob
      ? "bg-blue-100 border-blue-300"
      : "bg-green-100 border-green-300";
    const textColor = isJob ? "text-blue-800" : "text-green-800";

    return (
      <div
        key={`${item._type}-${item.id}`}
        onClick={() => handleItemClick(item)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleItemClick(item);
          }
        }}
        role="button"
        tabIndex={0}
        className={`text-xs p-1 mb-1 rounded border ${bgColor} ${textColor} truncate cursor-pointer hover:opacity-80 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-500`}
        title={
          isJob
            ? `${item.service?.name || "Service"} - ${
                item.customerName || "Customer"
              }`
            : `Quote - ${
                item.customerName || item.requestCustomerName || "Unknown"
              }`
        }
      >
        {isJob ? (
          <div>
            <div className="font-medium">
              {item.date_time
                ? DateUtils.formatTimeFromDate(item.date_time)
                : 'Time to be confirmed'}
            </div>
            <div className="font-medium">{item.service?.name || "Service"}</div>
            <div>{item.customer?.name || "Unknown Customer"}</div>
            <div>{item.property?.addressLine1 || "Unknown Address"}</div>
          </div>
        ) : (
          <div>
            <div className="font-medium">
              {item.requestedDateTime
                ? DateUtils.formatTimeFromDate(item.requestedDateTime)
                : 'Time to be confirmed'}
            </div>
            <div className="font-medium">{item.service?.name || "Service"}</div>
            <div>{item.customer?.name || "Unknown Customer"}</div>
            <div>{item.property?.addressLine1 || "Unknown Address"}</div>
          </div>
        )}
      </div>
    );
  };

  const renderMonthView = () => {
    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
      <div className="bg-white rounded-lg shadow">
        {/* Calendar Header */}
        <div className="grid grid-cols-7 border-b">
          {weekDays.map((day) => (
            <div
              key={day}
              className="p-3 text-center font-medium text-gray-500 border-r last:border-r-0"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {calendarDays.map((date) => {
            const items = getItemsForDate(date);
            const isCurrentMonth = isSameMonth(date, currentDate);
            const isCurrentDay = isToday(date);

            return (
              <div
                key={date.toISOString()}
                className={`min-h-[120px] p-2 border-r border-b last:border-r-0 ${
                  !isCurrentMonth ? "bg-gray-50" : "bg-white"
                } ${isCurrentDay ? "bg-blue-50" : ""}`}
              >
                <div
                  className={`text-sm font-medium mb-2 ${
                    !isCurrentMonth
                      ? "text-gray-400"
                      : isCurrentDay
                      ? "text-blue-600"
                      : "text-gray-900"
                  }`}
                >
                  {format(date, "d")}
                </div>
                <div className="space-y-1">{items.map(renderCalendarItem)}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
      <div className="bg-white rounded-lg shadow">
        {/* Week Header */}
        <div className="grid grid-cols-7 border-b">
          {calendarDays.slice(0, 7).map((date, index) => (
            <div
              key={date.toISOString()}
              className="p-4 text-center border-r last:border-r-0"
            >
              <div className="text-sm font-medium text-gray-500">
                {weekDays[index]}
              </div>
              <div
                className={`text-lg font-semibold ${
                  isToday(date) ? "text-blue-600" : "text-gray-900"
                }`}
              >
                {format(date, "d")}
              </div>
            </div>
          ))}
        </div>

        {/* Week Grid */}
        <div className="grid grid-cols-7">
          {calendarDays.slice(0, 7).map((date) => {
            const items = getItemsForDate(date);
            const isCurrentDay = isToday(date);

            return (
              <div
                key={date.toISOString()}
                className={`min-h-[200px] p-3 border-r last:border-r-0 ${
                  isCurrentDay ? "bg-blue-50" : "bg-white"
                }`}
              >
                <div className="space-y-2">{items.map(renderCalendarItem)}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const items = getItemsForDate(currentDate);

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                No appointments scheduled for this day
              </p>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={`${item._type}-${item.id}`}
                onClick={() => handleItemClick(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleItemClick(item);
                  }
                }}
                role="button"
                tabIndex={0}
                className="border rounded-lg p-4 cursor-pointer hover:bg-gray-50 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {renderCalendarItem(item)}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderCalendarView = () => {
    switch (viewMode) {
      case "month":
        return renderMonthView();
      case "week":
        return renderWeekView();
      case "day":
        return renderDayView();
      default:
        return renderMonthView();
    }
  };

  return (
    <div className="space-y-6">
      {/* Calendar Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Navigation */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateDate("prev")}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Today
            </button>
            <button
              onClick={() => navigateDate("next")}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Current Date Display */}
          <h2 className="text-xl font-semibold text-gray-900">
            {formatDateHeader()}
          </h2>
        </div>

        <div className="flex items-center space-x-4">
          {/* View Mode Selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {viewModes.map((mode) => (
              <button
                key={mode.id}
                onClick={() => setViewMode(mode.id)}
                className={`px-3 py-1 text-sm font-medium rounded ${
                  viewMode === mode.id
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>

          {/* Filters Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <Filter className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="flex items-center space-x-6">
            <span className="text-sm font-medium text-gray-700">Show:</span>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showJobs}
                onChange={(e) => setShowJobs(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700 flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
                Jobs
              </span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showQuotes}
                onChange={(e) => setShowQuotes(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700 flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                Quotes
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Calendar */}
      {renderCalendarView()}
    </div>
  );
}
